"use client";

import Link from "next/link";
import Image from "next/image";
import { MainLayout } from "@/components/layout/main-layout";
import { RouteGuard } from "@/components/auth/route-guard";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Docs4AI } from "@docs4ai/sdk";
import { useEffect, useState, useCallback } from "react";

function DocsLoadingSkeleton() {
  return (
    <section className="py-12 md:py-20 bg-background">
      <div className="container px-4 md:px-6">
        <div className="flex flex-col items-center justify-center space-y-4 text-center">
          <div className="space-y-2">
            <Skeleton className="h-10 w-96 mx-auto" />
            <Skeleton className="h-6 w-[700px] mx-auto" />
          </div>
          <div className="w-full max-w-md space-y-2">
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        </div>

        <div className="mt-16 grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {[...Array(8)].map((_, idx) => (
            <div
              key={idx}
              className="flex flex-col items-center rounded-lg border bg-card p-6 text-center"
            >
              <Skeleton className="h-16 w-16 rounded-full mb-4" />
              <Skeleton className="h-6 w-32 mb-2" />
              <div className="mt-auto flex flex-wrap gap-2 justify-center">
                {[...Array(3)].map((_, tagIdx) => (
                  <Skeleton
                    key={tagIdx}
                    className="h-5 w-16 rounded-full"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <section className="border-t bg-muted/30 py-12 mt-12">
        <div className="container px-4 md:px-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="space-y-2">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-5 w-96" />
            </div>
            <Skeleton className="h-10 w-40" />
          </div>
        </div>
      </section>
    </section>
  );
}

const FRAMEWORKS_CACHE_KEY = "docs4ai_frameworks_cache";
const CACHE_DURATION = 3600000; // 1 hour in milliseconds

interface Framework {
  name: string;
  versions: string[];
  tags: string[];
  iconUrl: string;
}

interface FrameworkApiResponse {
  frameworks: Framework[];
}

// Initialize SDK without API key for public access
const docs4ai = new Docs4AI({
  apiKey: "",
  baseUrl: process.env.NEXT_PUBLIC_DOCS4AI_BASE_URL!,
});

export default function DocsPage() {
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAndCacheFrameworks = useCallback(async () => {
    try {
      const response = await docs4ai.getFrameworks() as unknown as FrameworkApiResponse;
      console.log("getFrameworks API called");
      
      const cacheData = {
        frameworks: response.frameworks,
        timestamp: Date.now(),
        expiresIn: CACHE_DURATION
      };
      localStorage.setItem(FRAMEWORKS_CACHE_KEY, JSON.stringify(cacheData));
      
      return response.frameworks;
    } catch (err) {
      console.error("Error fetching frameworks:", err);
      throw err;
    }
  }, []);

  const getFrameworksFromCache = useCallback(() => {
    try {
      const cachedData = localStorage.getItem(FRAMEWORKS_CACHE_KEY);
      if (!cachedData) return null;

      const { frameworks, timestamp, expiresIn } = JSON.parse(cachedData);
      const isExpired = Date.now() - timestamp > expiresIn;

      if (isExpired) {
        localStorage.removeItem(FRAMEWORKS_CACHE_KEY);
        return null;
      }

      return frameworks;
    } catch (error) {
      console.error("Error reading from cache:", error);
      localStorage.removeItem(FRAMEWORKS_CACHE_KEY);
      return null;
    }
  }, []);

  useEffect(() => {
    async function fetchFrameworks() {
      try {
        const cachedFrameworks = getFrameworksFromCache();
        if (cachedFrameworks) {
          console.log("Using cached frameworks data");
          setFrameworks(cachedFrameworks);
          setError(null);
          setLoading(false);
          return;
        }

        const freshFrameworks = await fetchAndCacheFrameworks();
        setFrameworks(freshFrameworks);
        setError(null);
      } catch (err) {
        console.error("Error fetching frameworks:", err);
        setError("Failed to load frameworks");
      } finally {
        setLoading(false);
      }
    }

    fetchFrameworks();
  }, [fetchAndCacheFrameworks, getFrameworksFromCache]);

  return (
    <MainLayout>
      <RouteGuard>
      {loading ? (
        <DocsLoadingSkeleton />
      ) : error ? (
        <div className="flex h-96 items-center justify-center bg-background">
          <div className="text-lg text-red-500">{error}</div>
        </div>
      ) : (
        <>
          <section className="py-12 md:py-20 bg-background">
            <div className="container px-4 md:px-6">
              <div className="flex flex-col items-center justify-center space-y-4 text-center">
                <div className="space-y-2">
                  <h1 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
                    Framework Documentation
                  </h1>
                  <p className="max-w-[700px] text-muted-foreground md:text-xl">
                    Browse documentation for popular frameworks with AI-powered assistance
                  </p>
                </div>
                <div className="w-full max-w-md space-y-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <input
                      type="search"
                      placeholder="Search documentation..."
                      className="w-full rounded-md border border-input bg-background py-2 pl-8 pr-4 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-16 grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {frameworks.map((framework) => (
                  <Link
                    key={framework.name}
                    href={`/docs/${framework.name.toLowerCase()}/${framework.versions[0]}`}
                    className="group flex flex-col items-center rounded-lg border bg-card p-6 text-center shadow-sm transition-colors hover:bg-muted/50"
                  >
                    <div className="mb-4 rounded-full bg-primary/10 p-2">
                      <div className="relative h-12 w-12">
                        <Image
                          src={framework.iconUrl}
                          alt={framework.name}
                          fill
                          className="object-contain p-1"
                        />
                      </div>
                    </div>
                    <h3 className="mb-2 text-xl font-bold">{framework.name}</h3>
                    <div className="mt-auto flex flex-wrap gap-2 justify-center">
                      {framework.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="border-t bg-muted/30 py-12">
            <div className="container px-4 md:px-6">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="space-y-2">
                  <h2 className="text-2xl font-bold tracking-tight">
                    Need more documentation?
                  </h2>
                  <p className="text-muted-foreground">
                    Request additional frameworks or versions to be added to our documentation.
                  </p>
                </div>
                <Button>Request Framework</Button>
              </div>
            </div>
          </section>
        </>
      )}
      </RouteGuard>
    </MainLayout>
  );
}
